import { Card, Accordion, Text, Link, Icon, Separator, CardColors } from '@/ui'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useReselector } from '@/utils/sharedHooks'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectIsBasicPlan } from '@/reducers/subscription.slice'
import { Attributes, useAnalyticsView } from '@/features/amplitude'
import { useEffect } from 'react'
import { TypographyStyles } from '@/styles'

const HelpOption = ({
  title,
  description,
  linkText = 'Go',
  href,
  newPage,
  trackMessage,
  trackAttributes,
}: {
  title: string
  description: string
  linkText?: string
  href?: string
  newPage?: boolean
  trackMessage?: string
  trackAttributes?: Attributes
}) => {
  return (
    <div className="mb-4 mt-4">
      <Text variant={TypographyStyles.H3} className="text-neutral-700 mb-1">
        {title}
      </Text>
      <Text
        variant={TypographyStyles.BODY_SM}
        className="text-neutral-600 mb-1"
      >
        {description}{' '}
        {href && (
          <Link
            variant="noUnderline"
            href={href}
            newPage={newPage}
            trackMessage={trackMessage}
            trackAttributes={trackAttributes}
          >
            <b>{linkText}</b>
            <Icon icon={regular('arrow-right')} className="ml-1.5" size="sm" />
          </Link>
        )}
      </Text>
    </div>
  )
}

export const GetHelpCard = () => {
  const hasPhoneSupport = useReselector(
    selectUserHasPermission,
    ProductFeatureId.phoneSupport
  )
  // TODO: Once stripe entitlements are implemented, this check can be removed
  // ZEN-2064
  const isBasicPlan = useReselector(selectIsBasicPlan)
  const pageView = useAnalyticsView()

  useEffect(() => {
    pageView('viewed get help card')
  }, [pageView])

  return (
    <Card color={CardColors.LEVEL_0}>
      <Accordion variant="header" type="single" collapsible>
        <Accordion.Item value="help">
          <Accordion.Trigger>
            <div className="flex flex-col w-full">
              <Text variant={TypographyStyles.H2}>Get Help</Text>
              <Text
                variant={TypographyStyles.BODY_MD}
                className="text-neutral-500"
              >
                How to get help at Heard
              </Text>
            </div>
          </Accordion.Trigger>
          <Accordion.Content>
            <div className="space-y-4">
              <HelpOption
                title="Help Center"
                description="Search articles to address common and frequently asked questions."
                href="https://support.joinheard.com/hc/en-us"
                newPage
                trackMessage="clicked help center on get help card"
                trackAttributes={{ button: 'help center' }}
              />
              <Separator />
              <HelpOption
                title="Heard Community"
                description="Learn, connect, and find answers from fellow therapists in the Heard Community."
                href="https://support.joinheard.com/hc/en-us/community/topics"
                newPage
                trackMessage="clicked heard community on get help card"
                trackAttributes={{ button: 'heard community' }}
              />
              <Separator />
              <HelpOption
                title="Messages"
                description="Send a message to a member of our Support team to get personalized help."
                href="/conversations"
                trackMessage="clicked messages on get help card"
                trackAttributes={{ button: 'messages' }}
              />
              {(hasPhoneSupport || !isBasicPlan) && (
                <>
                  <Separator />
                  <HelpOption
                    title="Phone Support"
                    description="Call +1 (844) 775-0025 from 8 AM to 8 PM ET from Monday through Friday to reach one of our Tax & Accounting Specialists."
                  />
                </>
              )}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  )
}
